import type { OnEventTypeChangeProps } from "./asset-timeline-event-type-picker";
import type {
  AssetTimelineDateRange,
  AssetTimelineFilters,
} from "./asset-timeline.type";
import type { Int } from "@/types";

import { useMemo, useState } from "react";

import { useGetAsset } from "@/entities/asset";
import { ASSET_EVENTS_FILTERS } from "@/entities/asset-events";
import { useReferentialAssetEventTypes } from "@/entities/referential/referential-asset-event-types";
import { useGetAssetEventInfiniteList } from "@/hooks/queries/asset-event";

import {
  convertDatePickerValuesToFetchFilterValue,
  getTimelineItems,
  getTimelineStatus,
} from "./asset-timeline.helper";

export const useAssetTimelineData = (assetId: Int) => {
  const [filters, setFilters] = useState<AssetTimelineFilters>({});

  // Build fetchParams based on filters state
  const fetchParams = useMemo(
    () => ({
      "page[size]": 20,
      [ASSET_EVENTS_FILTERS.TYPE]: filters.type,
      [ASSET_EVENTS_FILTERS.SUB_TYPE]: filters.subType,
      [ASSET_EVENTS_FILTERS.OCCURRING_DATE_TIME]:
        convertDatePickerValuesToFetchFilterValue(filters.dateRange),
    }),
    [filters]
  );

  // Fetch all data needed
  const { assetEventTypes } = useReferentialAssetEventTypes();
  const { asset, ...assetQuery } = useGetAsset(assetId);
  const { assetEvents, ...assetEventsQuery } = useGetAssetEventInfiniteList(
    assetId,
    fetchParams
  );

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = assetEventsQuery;

  const items = useMemo(
    () => getTimelineItems({ asset, assetEvents, assetEventTypes, filters }),
    [asset, assetEvents, assetEventTypes]
  );

  const onLoadMore = () => {
    if (hasNextPage) fetchNextPage();
  };

  const onRangeChange = (dateRange: AssetTimelineDateRange) => {
    setFilters((existingFilters) => ({ ...existingFilters, dateRange }));
  };

  const onTypeChange = ({ type, subType }: OnEventTypeChangeProps) => {
    setFilters((existingFilters) => ({ ...existingFilters, type, subType }));
  };

  const queryStatus = useMemo(
    () => getTimelineStatus({ items, assetQuery, assetEventsQuery }),
    [items, assetQuery, assetEventsQuery]
  );

  return {
    items,
    ...queryStatus,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    onRangeChange,
    onTypeChange,
  };
};
